import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FlatList } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { captureCatch } from '../../shared/sentry/sentryUser';
import type { Product } from '../../catalog/domain-api';
import type { PosCatalogItemTypeFilter } from '../domain/posTypes';
import type { PosCatalogRow, PosPackageGridRow } from '../domain/posTypes';
import { listServicePackages } from '../../appointments/infra/servicePackagesRepo';
import { normalizeSearchText } from '../../shared/format/searchText';
import { useAuthStore } from '../../auth/store/useAuthStore';
import {
  POS_CATALOG_PAGE_SIZE_STORAGE_KEY,
  POS_CATALOG_WIDE_PAGE_SIZE_STORAGE_KEY,
  type PosCatalogPageSizePreset,
} from './posLayout.constants';
import { preflightCatalogSnapshotForRead } from '../../catalog/infra-api'
import { countSearchProducts, searchProducts } from '../../catalog/infra-api';
import { scrollPaginatedListToTop } from '../../shared/ui/paginatedListScroll';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';
import { homeDbg } from '../../shared/dev/homeDebug';
import {
  beginPosCatalogLoad,
  armPosCatalogInflight,
  completePosCatalogLoad,
  failPosCatalogLoad,
  getPosCatalogCachedProducts,
  invalidatePosCatalogLoadGate,
  isPosCatalogHydrated,
  isPosCatalogInflight,
  waitForPosCatalogLoad,
} from './posCatalogLoadGate';
import { planPosCatalogSyncedRefresh, shouldRefreshPosCatalogOnFocus } from './refreshPosLocalSurfaces';

const PAGE_SIZE_PRESETS: Record<
  PosCatalogPageSizePreset,
  { storageKey: string; defaultSize: number }
> = {
  compact: {
    storageKey: POS_CATALOG_PAGE_SIZE_STORAGE_KEY,
    defaultSize: 12,
  },
  wide: {
    storageKey: POS_CATALOG_WIDE_PAGE_SIZE_STORAGE_KEY,
    defaultSize: 18,
  },
};

export type PosProductRow = Awaited<ReturnType<typeof searchProducts>>[number];

/** Mapea service_packages a filas de grilla POS + filtro de búsqueda client-side. */
function servicePackagesToGridRows(
  packages: Awaited<ReturnType<typeof listServicePackages>>,
  searchQuery: string,
): PosPackageGridRow[] {
  const tokens = normalizeSearchText(searchQuery).split(/\s+/).filter(Boolean);
  return packages
    .map((pkg) => ({
      kind: 'package' as const,
      packageId: pkg.id,
      name: pkg.name,
      price: pkg.price,
      sessionCount: pkg.sessionCount,
      validityDays: pkg.validityDays,
    }))
    .filter((row) =>
      tokens.length === 0
        ? true
        : tokens.every((token) => normalizeSearchText(row.name).includes(token)),
    )
    .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
}

type UsePosCatalogPageParams = {
  currentBranchId: string | null | undefined;
  selectedCategory: Product['categoryId'] | 'all';
  searchQuery: string;
  itemTypeFilter: PosCatalogItemTypeFilter;
  posPhysicalStockPositive: boolean;
  brandId?: string | null;
  supplierId?: string | null;
  /** Rail: allowlist de ids (Favoritos/Combos/En oferta). null = sin filtro. */
  posProductIds?: ReadonlySet<string> | null;
  pageSizePreset?: PosCatalogPageSizePreset;
  /** Opciones dinámicas (filas completas × columnas). */
  pageSizeOptions?: readonly number[];
  pageSizeDefault?: number;
  /**
   * PC desk: page size driven by measured viewport (complete rows).
   * When set, ignores AsyncStorage presets.
   */
  pageSizeControlled?: number | null;
};

/**
 * Catálogo POS con páginas discretas alineadas a filas completas de la grilla.
 */
export const usePosCatalogPage = ({
  currentBranchId,
  selectedCategory,
  searchQuery,
  itemTypeFilter,
  posPhysicalStockPositive,
  brandId = null,
  supplierId = null,
  posProductIds = null,
  pageSizePreset = 'compact',
  pageSizeOptions,
  pageSizeDefault,
  pageSizeControlled = null,
}: UsePosCatalogPageParams) => {
  const isFocused = useIsFocused();
  const isFocusedRef = useRef(isFocused);
  const wasFocusedRef = useRef(false);
  isFocusedRef.current = isFocused;

  const productListRef = useRef<FlatList<PosCatalogRow>>(null);
  const opGenRef = useRef(0);
  const hasLoadedCatalogOnceRef = useRef(false);
  const lastPreflightBranchKeyRef = useRef<string | null>(null);
  const preflightCompletedRef = useRef(false);
  const scrollListToTopRef = useRef(false);
  const prevCriteriaKeyRef = useRef('');
  const prevFilterCriteriaKeyRef = useRef('');

  const [products, setProducts] = useState<PosCatalogRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogPageIndex, setCatalogPageIndex] = useState(0);
  const preset = PAGE_SIZE_PRESETS[pageSizePreset];
  const controlledPageSize =
    typeof pageSizeControlled === 'number' && pageSizeControlled > 0 ? pageSizeControlled : null;
  const resolvedPageSizeOptions = useMemo(
    () => (pageSizeOptions?.length ? pageSizeOptions : ([8, 12] as const)),
    [pageSizeOptions]
  );
  const resolvedPageSizeDefault = pageSizeDefault ?? preset.defaultSize;

  const [pageSizeState, setPageSizeState] = useState<number>(
    controlledPageSize ?? resolvedPageSizeDefault
  );
  const [pageSizeHydrated, setPageSizeHydrated] = useState(controlledPageSize != null);
  const [hasMore, setHasMore] = useState(false);
  const [catalogTotalCount, setCatalogTotalCount] = useState<number | null>(null);
  const [catalogCriteriaRefreshing, setCatalogCriteriaRefreshing] = useState(false);
  const [reloadBurst, setReloadBurst] = useState(0);
  const catalogSyncedDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCatalogSyncedRef = useRef(false);
  const runSilentCatalogRefreshRef = useRef<() => Promise<void>>(async () => undefined);
  const sessionUserId = useAuthStore((s) => s.session?.user?.id ?? null);

  const searchParams = useMemo(
    () => ({
      categoryId: selectedCategory,
      search: searchQuery,
      limit: pageSizeState,
      offset: catalogPageIndex * pageSizeState,
      branchId: currentBranchId ?? null,
      posExcludeMisc: true as const,
      posItemType: itemTypeFilter === 'package' ? 'all' : itemTypeFilter,
      posPhysicalStockPositive: Boolean(currentBranchId) && posPhysicalStockPositive,
      brandId,
      supplierId,
      posProductIds,
    }),
    [
      brandId,
      catalogPageIndex,
      currentBranchId,
      itemTypeFilter,
      pageSizeState,
      posPhysicalStockPositive,
      posProductIds,
      searchQuery,
      selectedCategory,
      supplierId,
    ],
  );

  const queryCatalogRows = useCallback(async (): Promise<PosCatalogRow[]> => {
    if (itemTypeFilter === 'package') {
      // Paquetes: otra fuente (service_packages), sin paginación ni filtros de catálogo.
      const packages = await listServicePackages(false, currentBranchId ?? null);
      return servicePackagesToGridRows(packages, searchQuery);
    }
    return searchProducts(searchParams);
  }, [currentBranchId, itemTypeFilter, searchParams, searchQuery]);

  const filterCriteriaKey = useMemo(
    () =>
      JSON.stringify([
        sessionUserId,
        currentBranchId,
        selectedCategory,
        searchQuery,
        itemTypeFilter,
        posPhysicalStockPositive,
        brandId,
        supplierId,
        posProductIds ? [...posProductIds].sort().join(',') : null,
      ]),
    [
      sessionUserId,
      currentBranchId,
      itemTypeFilter,
      posPhysicalStockPositive,
      posProductIds,
      searchQuery,
      selectedCategory,
      brandId,
      supplierId,
    ]
  );

  const criteriaKey = useMemo(
    () => JSON.stringify([filterCriteriaKey, catalogPageIndex, pageSizeState]),
    [catalogPageIndex, filterCriteriaKey, pageSizeState]
  );
  const criteriaKeyRef = useRef(criteriaKey);
  criteriaKeyRef.current = criteriaKey;

  useEffect(() => {
    if (controlledPageSize != null) {
      setPageSizeState(controlledPageSize);
      setPageSizeHydrated(true);
      return;
    }
    let alive = true;
    setPageSizeHydrated(false);
    void AsyncStorage.getItem(preset.storageKey).then((raw) => {
      if (!alive) return;
      const parsed = Number(raw);
      if (resolvedPageSizeOptions.includes(parsed)) {
        setPageSizeState(parsed);
      } else {
        setPageSizeState(
          resolvedPageSizeOptions.includes(resolvedPageSizeDefault)
            ? resolvedPageSizeDefault
            : resolvedPageSizeOptions[resolvedPageSizeOptions.length - 1] ?? resolvedPageSizeDefault
        );
      }
      setPageSizeHydrated(true);
    });
    return () => {
      alive = false;
    };
  }, [
    controlledPageSize,
    pageSizePreset,
    preset.storageKey,
    resolvedPageSizeDefault,
    resolvedPageSizeOptions,
  ]);

  useEffect(() => {
    if (controlledPageSize != null || !pageSizeHydrated) return;
    setPageSizeState((current) => {
      if (resolvedPageSizeOptions.includes(current)) return current;
      const fallback = resolvedPageSizeOptions.includes(resolvedPageSizeDefault)
        ? resolvedPageSizeDefault
        : resolvedPageSizeOptions[resolvedPageSizeOptions.length - 1] ?? resolvedPageSizeDefault;
      void AsyncStorage.setItem(preset.storageKey, String(fallback));
      return fallback;
    });
  }, [
    controlledPageSize,
    pageSizeHydrated,
    preset.storageKey,
    resolvedPageSizeDefault,
    resolvedPageSizeOptions,
  ]);

  const prevPageSizeRef = useRef(pageSizeState);
  useLayoutEffect(() => {
    let resetPageIndex = false;
    if (prevFilterCriteriaKeyRef.current !== filterCriteriaKey) {
      prevFilterCriteriaKeyRef.current = filterCriteriaKey;
      scrollListToTopRef.current = true;
      resetPageIndex = true;
    }
    if (prevCriteriaKeyRef.current !== criteriaKey) {
      prevCriteriaKeyRef.current = criteriaKey;
      if (!resetPageIndex) {
        scrollListToTopRef.current = true;
      }
      const cached = getPosCatalogCachedProducts<PosCatalogRow>(criteriaKey);
      if (cached) {
        setProducts(cached);
        hasLoadedCatalogOnceRef.current = true;
        setCatalogLoading(false);
      } else if (!isPosCatalogHydrated(criteriaKey)) {
        // Rail/categoría: mantener grilla anterior evita parpadeo (Combos, Favoritos…).
        if (!hasLoadedCatalogOnceRef.current) {
          setProducts([]);
          setCatalogLoading(true);
        }
      }
    }
    if (prevPageSizeRef.current !== pageSizeState) {
      prevPageSizeRef.current = pageSizeState;
      scrollListToTopRef.current = true;
      resetPageIndex = true;
      if (!isPosCatalogHydrated(criteriaKey)) {
        hasLoadedCatalogOnceRef.current = false;
      }
    }
    if (resetPageIndex) {
      setCatalogPageIndex(0);
    }
  }, [criteriaKey, filterCriteriaKey, pageSizeState]);

  const refreshCatalogTotalCount = useCallback(async () => {
    if (itemTypeFilter === 'package') {
      // Sin paginación: la lista completa ya viene en queryCatalogRows.
      setCatalogTotalCount(null);
      setHasMore(false);
      return;
    }
    try {
      const n = await countSearchProducts({
        categoryId: selectedCategory,
        search: searchQuery,
        branchId: currentBranchId ?? null,
        includeInactive: false,
        posExcludeMisc: true,
        posItemType: itemTypeFilter,
        posPhysicalStockPositive: Boolean(currentBranchId) && posPhysicalStockPositive,
        brandId,
        supplierId,
        posProductIds,
      });
      setCatalogTotalCount(n);
    } catch (error) {
      console.warn('[POS] No se pudo contar ítems del catálogo', error);
      setCatalogTotalCount(null);
    }
  }, [currentBranchId, itemTypeFilter, posPhysicalStockPositive, posProductIds, searchQuery, selectedCategory, brandId, supplierId]);

  useEffect(() => {
    void refreshCatalogTotalCount();
  }, [refreshCatalogTotalCount]);

  const fetchCatalogRows = useCallback(async (): Promise<PosCatalogRow[]> => {
    const branchKey = currentBranchId ?? 'none';
    if (lastPreflightBranchKeyRef.current !== branchKey) {
      lastPreflightBranchKeyRef.current = branchKey;
      preflightCompletedRef.current = false;
      homeDbg('POS.catalog', 'preflight → start', { branchKey });
      void preflightCatalogSnapshotForRead()
        .catch(captureCatch('pos', 'catalog-page-preflight'))
        .finally(() => {
          preflightCompletedRef.current = true;
          homeDbg('POS.catalog', 'preflight → done', { branchKey });
          if (!hasLoadedCatalogOnceRef.current && isFocusedRef.current) {
            setReloadBurst((b) => b + 1);
          }
        });
    }
    return queryCatalogRows();
  }, [currentBranchId, queryCatalogRows]);

  const runSilentCatalogRefresh = useCallback(async () => {
    if (!isFocusedRef.current) {
      homeDbg('POS.catalog', 'silent refresh skip — blurred');
      return;
    }
    try {
      await refreshCatalogTotalCount();
      const rows = await queryCatalogRows();
      // El callback puede ser invocado con delay (debounce 250ms) y traer
      // criterios viejos: si la criteria cambió en el medio, no pisar la grilla.
      if (criteriaKeyRef.current !== criteriaKey) {
        homeDbg('POS.catalog', 'silent refresh descartado — criteria cambió mid-flight');
        return;
      }
      completePosCatalogLoad(criteriaKey, rows);
      setProducts(rows);
      hasLoadedCatalogOnceRef.current = true;
    } catch (error) {
      console.warn('[POS] No se pudo refrescar catálogo tras sync', error);
    }
  }, [criteriaKey, queryCatalogRows, refreshCatalogTotalCount]);
  runSilentCatalogRefreshRef.current = runSilentCatalogRefresh;

  const drainPendingCatalogSynced = useCallback(() => {
    if (!pendingCatalogSyncedRef.current) return;
    pendingCatalogSyncedRef.current = false;
    void runSilentCatalogRefreshRef.current();
  }, []);

  useEffect(() => {
    if (!pageSizeHydrated || !sessionUserId) {
      return;
    }

    const loadMode = beginPosCatalogLoad(criteriaKey);
    if (loadMode === 'deduped') {
      const cached = getPosCatalogCachedProducts<PosCatalogRow>(criteriaKey);
      if (cached) {
        setProducts(cached);
        setCatalogLoading(false);
        return;
      }
      void waitForPosCatalogLoad<PosCatalogRow>(criteriaKey)
        .then((rows) => {
          if (rows.length > 0) {
            setProducts(rows);
            setCatalogLoading(false);
          } else if (preflightCompletedRef.current || hasLoadedCatalogOnceRef.current) {
            setCatalogLoading(false);
          }
          drainPendingCatalogSynced();
        })
        .catch(() => {
          if (preflightCompletedRef.current || hasLoadedCatalogOnceRef.current) {
            setCatalogLoading(false);
          }
        });
      return;
    }

    if (loadMode === 'background') {
      const cached = getPosCatalogCachedProducts<PosCatalogRow>(criteriaKey);
      if (cached) {
        setProducts(cached);
        hasLoadedCatalogOnceRef.current = true;
        setCatalogLoading(false);
      }
      armPosCatalogInflight(criteriaKey);
    }

    const opId = ++opGenRef.current;
    const backgroundRefresh =
      loadMode === 'background' ||
      (loadMode === 'initial' && hasLoadedCatalogOnceRef.current);
    if (!backgroundRefresh) {
      setCatalogLoading(true);
    }
    setCatalogCriteriaRefreshing(backgroundRefresh);

    const run = async () => {
      let loadedRows: PosCatalogRow[] = [];
      try {
        loadedRows = await fetchCatalogRows();

        completePosCatalogLoad(criteriaKey, loadedRows);
        drainPendingCatalogSynced();

        if (opId !== opGenRef.current) {
          return;
        }

        setProducts(loadedRows);
        hasLoadedCatalogOnceRef.current =
          loadedRows.length > 0 || preflightCompletedRef.current;
        if (scrollListToTopRef.current) {
          scrollPaginatedListToTop({ flatListRef: productListRef }, true);
          scrollListToTopRef.current = false;
        }
      } catch (error) {
        failPosCatalogLoad(criteriaKey, error);
        if (opId !== opGenRef.current) {
          return;
        }
        console.warn('[POS] No se pudo cargar el catálogo', error);
        setProducts([]);
        setHasMore(false);
      } finally {
        if (opId === opGenRef.current) {
          if (loadedRows.length === 0 && !preflightCompletedRef.current && !hasLoadedCatalogOnceRef.current) {
            // Preflight not yet completed and no products loaded — keep spinner
          } else {
            setCatalogLoading(false);
            setCatalogCriteriaRefreshing(false);
          }
        }
      }
    };

    void run();

    return () => {
      opGenRef.current += 1;
    };
  }, [
    catalogPageIndex,
    criteriaKey,
    drainPendingCatalogSynced,
    fetchCatalogRows,
    pageSizeHydrated,
    pageSizeState,
    reloadBurst,
    sessionUserId,
  ]);

  useEffect(() => {
    if (catalogTotalCount === null || catalogLoading) {
      return;
    }
    const offset = catalogPageIndex * pageSizeState;
    setHasMore(offset + products.length < catalogTotalCount);
  }, [catalogLoading, catalogPageIndex, catalogTotalCount, pageSizeState, products.length]);

  const setCatalogPageSize = useCallback(
    (size: number) => {
      if (controlledPageSize != null) return;
      if (!resolvedPageSizeOptions.includes(size)) {
        return;
      }
      setPageSizeState(size);
      void AsyncStorage.setItem(preset.storageKey, String(size));
    },
    [controlledPageSize, preset.storageKey, resolvedPageSizeOptions]
  );

  const loadCatalogNextPage = useCallback(() => {
    if (!hasMore || catalogLoading) {
      return;
    }
    setCatalogPageIndex((p) => p + 1);
  }, [catalogLoading, hasMore]);

  const loadCatalogPreviousPage = useCallback(() => {
    if (catalogLoading) {
      return;
    }
    setCatalogPageIndex((p) => (p <= 0 ? 0 : p - 1));
  }, [catalogLoading]);

  const reloadCatalog = useCallback(async () => {
    await refreshCatalogTotalCount();
    invalidatePosCatalogLoadGate();
    setReloadBurst((b) => b + 1);
    setCatalogPageIndex(0);
  }, [refreshCatalogTotalCount]);

  useEffect(() => {
    const becameFocused = shouldRefreshPosCatalogOnFocus(wasFocusedRef.current, isFocused);
    wasFocusedRef.current = isFocused;
    if (!becameFocused) return;
    homeDbg('POS.catalog', 'focus → re-read RAM catalog');
    invalidatePosCatalogLoadGate();
    setReloadBurst((b) => b + 1);
  }, [isFocused]);

  useEffect(() => {
    const unsubscribeCatalogSynced = subscribeCatalogSynced(() => {
      const plan = planPosCatalogSyncedRefresh({
        isFocused: isFocusedRef.current,
        isInflight: isPosCatalogInflight(criteriaKey),
      });
      if (plan === 'invalidate-only') {
        invalidatePosCatalogLoadGate();
        return;
      }
      if (plan === 'defer') {
        pendingCatalogSyncedRef.current = true;
        return;
      }
      if (catalogSyncedDebounceRef.current) {
        clearTimeout(catalogSyncedDebounceRef.current);
      }
      catalogSyncedDebounceRef.current = setTimeout(() => {
        catalogSyncedDebounceRef.current = null;
        if (!isFocusedRef.current) return;
        homeDbg('POS.catalog', 'catalogSynced → refresh');
        if (!isPosCatalogHydrated(criteriaKey)) {
          setReloadBurst((b) => b + 1);
          return;
        }
        void runSilentCatalogRefreshRef.current();
      }, 250);
    });
    return () => {
      if (catalogSyncedDebounceRef.current) {
        clearTimeout(catalogSyncedDebounceRef.current);
        catalogSyncedDebounceRef.current = null;
      }
      unsubscribeCatalogSynced();
    };
  }, [criteriaKey, runSilentCatalogRefresh]);

  return {
    productListRef,
    products,
    catalogLoading,
    catalogCriteriaRefreshing,
    hasMore,
    catalogTotalCount,
    reloadCatalog,
    catalogPageIndex,
    catalogPageSize: pageSizeState,
    setCatalogPageSize,
    loadCatalogNextPage,
    loadCatalogPreviousPage,
  };
};
